import { mediaQueryUp } from 'components/utils/breakpoint'
import Modal from 'LuxKit/Modal/Modal'
import SegmentedOptionsGroup from 'LuxKit/SegmentedOptions/SegmentedOptionsGroup'
import SegmentedOption from 'LuxKit/SegmentedOptions/SegmentedOption'
import { rem } from 'polished'
import type { ComponentProps } from 'react'
import { useCallback, useState } from 'react'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import NavBarLanguageItems from './NavBarLanguageItems'
import NavBarRegionItems from './NavBarRegionItems'

const SegmentedOptionsWrapper = styled.div`
  display: flex;
  justify-content: center;

  > * {
    inline-size: min(100%, ${rem(360)});
  }

  > .distribution-filled > fieldset {
    min-inline-size: 0;
  }

  > .distribution-filled > fieldset > .distribution-filled {
    grid-auto-columns: minmax(0, 1fr);
  }
`

const Content = styled.div`
  padding-top: ${rem(16)};
`

const NavBarCurrencyLanguageDialog = styled(Modal)`
  ${mediaQueryUp.tablet} {
    &&& {
      --nav-bar-currency-language-dialog-size: min(
        calc(100vw - ${rem(40)}),
        calc(100dvh - ${rem(40)}),
        ${rem(560)}
      );

      width: var(--nav-bar-currency-language-dialog-size) !important;
      max-width: var(--nav-bar-currency-language-dialog-size) !important;
      height: var(--nav-bar-currency-language-dialog-size) !important;
      max-height: var(--nav-bar-currency-language-dialog-size) !important;
    }
  }
`

interface Props {
  showLanguageTab: boolean
  tabletHeight?: ComponentProps<typeof NavBarCurrencyLanguageDialog>['tabletHeight']
}

function NavBarCurrencyLanguageModal(props: Props) {
  const { showLanguageTab, tabletHeight } = props
  const intl = useIntl()
  const { resolve } = useModalElementContext()
  const [activeTab, setActiveTab] = useState<'currency' | 'language'>('currency')
  const [loading, setLoading] = useState(false)

  const handleValueChange = useCallback((value: 'currency' | 'language') => {
    setActiveTab(value)
  }, [])

  const handleSelect = useCallback((changed: boolean) => {
    if (changed) {
      setLoading(true)
    } else {
      resolve()
    }
  }, [resolve])

  const handleRegionSelect = useCallback((_event: React.MouseEvent<HTMLButtonElement>, changed: boolean) => {
    handleSelect(changed)
  }, [handleSelect])

  const title = showLanguageTab
    ? intl.formatMessage({
        id: 'niQoVEqXi3LmIJ_1E7Cc',
        defaultMessage: 'Region, currency & language',
      })
    : intl.formatMessage({
        id: 'jZ_em4vQCeUJW4EVMsbw',
        defaultMessage: 'Select your region & currency',
      })

  return (
    <NavBarCurrencyLanguageDialog
      height="full"
      size="S"
      tabletHeight={tabletHeight}
      title={title}
      loading={loading}
    >
      {showLanguageTab && <SegmentedOptionsWrapper>
        <SegmentedOptionsGroup<'currency' | 'language'>
          inputsName="nav-bar-currency-language-tab"
          onValueChange={handleValueChange}
          distribution="filled"
        >
          <SegmentedOption
            value="currency"
            checked={activeTab === 'currency'}
            title={
              <FormattedMessage
                id="5IzESyiJhZD7FXx2DaTM"
                defaultMessage="Region & Currency"
                
              />
            }
          />
          <SegmentedOption
            value="language"
            checked={activeTab === 'language'}
            title={
              <FormattedMessage
                id="_eRiayfEzUbUZ4Bbpv7r"
                defaultMessage="Language"
                
              />
            }
          />
        </SegmentedOptionsGroup>
      </SegmentedOptionsWrapper>}
      <Content>
        {activeTab === 'currency' && <NavBarRegionItems onRegionSelect={handleRegionSelect} />}
        {activeTab === 'language' && showLanguageTab && <NavBarLanguageItems onLanguageSelect={handleSelect} />}
      </Content>
    </NavBarCurrencyLanguageDialog>
  )
}

export default React.memo(NavBarCurrencyLanguageModal)
